<script lang="ts">
	//Design system components
	import { Button, Container, Modal } from '@Shelly/shelly-design-system';
	//Services
	import { format } from 'svelte-i18n';
	import { postReq } from 'src/services/http';
	//Helpers
	import { capFirstChar } from 'src/helpers/utilities';
	//Stores
	import { user_data } from 'src/stores/account/user';
	import { devices } from 'src/stores/lists/devices';
	//Types
	import type { Device } from 'src/types/Device';
	import { NotifType } from 'src/types/DeviceNotifPermissions';
	//Components
	import PushMailPermissions from '../DevicesSettings/ApplicationSettings/NotificationSettings/PushMailPermissions.svelte';
	import NonPremiumUserAlert from '../DevicesSettings/ApplicationSettings/NotificationSettings/NonPremiumUserAlert.svelte';
	import { onMount } from 'svelte';
	import { resetEditUpdateStore } from 'src/stores/ui/edit_lists';

	export let showModal: boolean;
	export let selectedDevices: Device[];
	export let notification: string = 'offline'; //optional to use other notifications also

	const isPremiumUser = $user_data?.premium > 0;

	let loading = false;
	let notifType;

	const notifications = {
		[notification]: {
			[NotifType.MAIL]: false,
			[NotifType.PUSH]: false,
		},
	}; //setting default values

	const setNotificationPerType = async (value: number, type: NotifType) => {
		//value 0 or 1
		//type 0 or 1 aka  NotifType.MAIL or NotifType.PUSH
		loading = true;
		const res = await postReq('interface/device/bulk_device_notifications', {
			devices: JSON.stringify(Object.keys(selectedDevices)),
			set: notification,
			val: value ? 1 : 0,
			ntype: type,
		});

		if (res?.isok) {
			Object.keys(selectedDevices).forEach(async (id) => {
				const device = $devices[id];
				if (typeof device?.notif_permissions !== 'undefined') {
					device.notif_permissions[notification][type] = value;
				} else {
					device.notif_permissions = {};
					device.notif_permissions[notification] = {};
					device.notif_permissions[notification][type] = value;
				}
				devices.update(device); //manually update device permissions
			});
		} else {
			notifications[notification][type] = !value;
		}
		loading = false;
		showModal = false;
		// resetFiltersAllDevicesList();
		resetEditUpdateStore(); //closing batch update mode
		return res;
	};

	const changeNotificationSetting = async (e) => {
		({ notification, notifType } = e.detail); //getting notification and notification type from event {notification:'offline', notifType: 0 or 1 aka  NotifType.MAIL or NotifType.PUSH}

		//changing the value of the notification type
		notifications[notification][notifType] = !notifications[notification][notifType];
	};

	const saveNotifications = async () => {
		// saving notifications per notification type mail and push
		Object.keys(notifications).forEach(async (_notification) => {
			Object.values(notifications[_notification]).forEach(async (value, i) => {
				await setNotificationPerType(value, i);
			});
		});
	};

	const checkForSettings = (setting) => {
		//checking if all devices have the same setting
		return Object.keys(selectedDevices).every((id) => {
			const device = $devices[id];
			if (device.id.includes(id)) {
				return device?.notif_permissions?.[notification]?.[setting];
			}
			return false;
		});
	};
	onMount(() => {
		//checking for settings if all devices have the same setting value for mail and push and then setting that value if so
		notifications[notification][NotifType.MAIL] = checkForSettings(NotifType.MAIL);

		notifications[notification][NotifType.PUSH] = checkForSettings(NotifType.PUSH);
	});
</script>

<Modal
	on:close={() => (showModal = false)}
	{showModal}
	title={capFirstChar(
		$format(`sub_settings_title_${notification}_notifications`, {
			default: 'offline notifications',
		}),
	)}
>
	<div slot="content">
		{#if isPremiumUser}
			<Container class="mb-m" noScroll padding="0">
				{#if typeof notifications !== 'undefined'}
					<PushMailPermissions
						bind:mail={notifications[notification][NotifType.MAIL]}
						bind:push={notifications[notification][NotifType.PUSH]}
						{notification}
						{loading}
						notifPermissionsBool={notifications}
						on:changeNotificationSetting={changeNotificationSetting}
					/>
				{/if}
			</Container>
		{:else}
			<NonPremiumUserAlert device={selectedDevices[0]} />
		{/if}
	</div>
	<div slot="footer" class="d-flex justify-content-end">
		{#if isPremiumUser}
			<Button
				variant="secondary"
				class="min-width-md-m min-width-sm-auto width-md-auto width-sm-full text-nowrap mr-s"
				on:click={() => (showModal = false)}
			>
				{$format('button_cancel', { default: 'Cancel' })}
			</Button>
			<Button
				class="min-width-md-m min-width-sm-auto width-md-auto width-sm-full text-nowrap"
				{loading}
				on:click={saveNotifications}
			>
				{$format('button_save', { default: 'Save' })}
			</Button>
		{/if}
	</div>
</Modal>
